import { CheckCircle } from '@trm/ui-kit/icons';
import { cx } from '@trm/ui-kit/utils';
import { ReactNode } from 'react';
import registrationImg from '~/assets/images/auth/registration_new.png';
import logo from '~/assets/images/auth/topstep_logo_new.svg';
import '~/pages/auth/auth-shared.css';

import { SocialIcons } from './components/SocialIcons';

/**
 * Props for the AuthWrapperRegisterNew component.
 *
 * @property children - The registration form content to render in the right panel
 */
interface Props {
  children: ReactNode;
}

const HEADLINE_TEXT_NEUE = 'Learn to trade. Earn payouts.';
const TAGLINE_NEUE = 'A proven path designed to support traders.';

const benefitsNeue = [
  {
    text: 'Trade our money while you learn',
  },
  {
    text: 'Unlock larger opportunities as you perform',
  },
  {
    text: 'Earn real payouts and keep up to 90% of the profits',
  },
];

const FOOTER_LINKS = [
  { href: 'https://www.topstep.com/help/', text: 'Help Center' },
  { href: 'https://www.topstep.com/privacy-policy/', text: 'Privacy Policy' },
  { href: 'https://www.topstep.com/terms-of-use/', text: 'Terms of Use' },
];

const BenefitsList = ({ className }: { className?: string }) => (
  <ul className={className}>
    {benefitsNeue.map((benefit, index) => (
      <li key={index} className="flex items-center gap-1">
        <CheckCircle className="size-4 shrink-0 text-white" />
        <span className="text-md">{benefit.text}</span>
      </li>
    ))}
  </ul>
);

const FooterLinks = ({ className }: { className?: string }) => (
  <div className={className}>
    {FOOTER_LINKS.map((link) => (
      <a
        key={link.text}
        href={link.href}
        target="_blank"
        rel="noopener noreferrer"
        className="hover:text-blue-400"
      >
        {link.text}
        <span className="sr-only"> (opens in a new tab)</span>
      </a>
    ))}
  </div>
);

const MarketingContentNew = () => (
  <>
    <h1 className="mb-4 text-display-md leading-tight font-bold">{HEADLINE_TEXT_NEUE}</h1>
    <p className="mb-2 text-xl">{TAGLINE_NEUE}</p>
    <BenefitsList className="mb-20 space-y-1 text-md" />
  </>
);

/**
 * Layout wrapper for the new trader registration flow.
 *
 * Renders marketing content on the left and the registration surface (children)
 * on the right, with a max-width container of 1440px split equally (720px each side).
 *
 * Related: TRMT-627 - UX Adjustments for Onboarding
 */
const AuthWrapperRegisterNew = ({ children }: Props) => {
  return (
    <div className="min-h-dvh bg-gradient-brand-primary lg:h-dvh lg:overflow-hidden">
      {/* Centered container with max-width 1440px */}
      <div className="mx-auto h-full max-w-360">
        <div className="flex h-full flex-col lg:flex-row">
          <div className="contents md:px-15 lg:relative lg:isolate lg:flex lg:w-120 lg:flex-col lg:justify-between lg:p-15 lg:text-white xl:w-180">
            {/* Background image — only visible at xl+ */}
            <div
              className="pointer-events-none absolute inset-0 -z-10 hidden xl:block"
              style={{
                backgroundImage: `linear-gradient(180deg, rgba(11, 15, 22, 0.00) 29.44%, #000 78.83%, #000 100%), url(${registrationImg})`,
                backgroundPosition: 'center 20%',
                backgroundSize: 'contain',
                backgroundRepeat: 'no-repeat',
              }}
            />
            <div
              className={`order-1 hidden self-center px-4 pt-12 pb-10 sm:self-start sm:px-8 sm:py-8 md:px-16 md:py-8 lg:order-0 lg:flex lg:p-0`}
            >
              <img src={logo} alt="Topstep logo" className="h-8 w-auto" />
            </div>

            <div
              className={`order-3 hidden px-4 py-10 text-white sm:block sm:px-12 md:px-16 lg:order-0 lg:p-0`}
            >
              <MarketingContentNew />
              <div className={`order-4 px-4 pb-10 text-white lg:order-0 lg:p-0`}>
                <div className="mb-6 border-t border-gray-700 lg:hidden" />
                <SocialIcons className="mb-6 flex gap-6 lg:mb-8" />
                <FooterLinks className="flex gap-6 text-xs lg:mt-4 lg:gap-4" />
              </div>
            </div>
          </div>

          {/* Right side - Registration form (720px on large screens) */}
          <div
            className={cx(
              'order-2 flex min-h-0 w-full flex-1 items-center justify-center overflow-hidden px-4 sm:px-12 md:px-16 lg:order-0 lg:px-0 lg:py-12 lg:pr-10 xl:w-180',
            )}
          >
            <div className="flex h-full w-full flex-col sm:border sm:border-[var(--auth-border-subtle)] lg:h-auto lg:max-h-full lg:w-150 lg:overflow-hidden lg:bg-[var(--auth-bg-primary-subtle)]">
              {children}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthWrapperRegisterNew;
